import React, { useRef, useCallback, useEffect, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useForm, FormProvider } from "react-hook-form";
import { makeRequest } from "@/makeRequest";
import { MY_ENDPOINTS } from "@raashidh/shared";
import AnnouncementCard from "./AnnouncementCard";
import { useLang } from "../context/TranslationProvider";
import MySwitch from "@/components/MySwitch";
import { mvOffices } from "./mvOffices";
import { mvIulaanTypes } from "./mvIulaanTypes";
import SearchableSelectField from "@/MY_COMPONENTS/MY_FORM/fields/SearchableSelectField";
import FilterButtons from "@/MY_COMPONENTS/FilterButtons";
import { MyHorizontalScrollable } from "@/MY_COMPONENTS/MyHorizontalScrollable";
import IconButton from "@/components/actionButtons/IconButton";
import MyLabel from "@/components/MyLabel";
import MyDivider from "@/components/MyDivider";

type Announcement = {
  page: number;
  type: string;
  office: string;
  title: string;
  link: string;
  date: string;
  deadline: string;
  postedAgo: string;
};

interface FilterFormData {
  page: string;
  office: string;
  type: string;
  "start-date": string;
  "end-date": string;
  "open-only": boolean;
  q: string;
}

export default function GazetteAnnouncementsPage() {
  const { t, direction } = useLang();
  const isRTL = direction === 'rtl';
  const loaderRef = useRef<HTMLDivElement>(null);
  const [autoLoad, setAutoLoad] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  
  const methods = useForm<FilterFormData>({
    defaultValues: {
      page: "",
      office: "",
      type: "",
      "start-date": "",
      "end-date": "",
      "open-only": false,
      q: "",
    }
  });

  const { watch, setValue, getValues } = methods;
  const filters = watch();

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isFetching,
    refetch,
  } = useInfiniteQuery({
    queryKey: ['gazette-announcements', filters],
    queryFn: async ({ pageParam = 1 }) => {
      const queryParams: Record<string, string | number | boolean> = {
        page: pageParam,
      };
      
      if (filters.office) queryParams.office = filters.office;
      if (filters.type) queryParams.type = filters.type;
      if (filters["start-date"]) queryParams["start-date"] = filters["start-date"];
      if (filters["end-date"]) queryParams["end-date"] = filters["end-date"];
      if (filters.q) queryParams.q = filters.q;
      if (filters["open-only"]) queryParams["open-only"] = "1";
      
      const response = await makeRequest<{
        success: boolean;
        data: Announcement[];
        nextCursor: number;
        hasMore: boolean;
      }>(MY_ENDPOINTS.SCRAPER_ENDPOINTS.GET_GAZETTE_ANNOUNCEMENTS.path, {
        method: "GET",
        query: queryParams,
        auth: true,
      });
      
      return response;
    },
    getNextPageParam: (lastPage) => {
      return lastPage.hasMore ? lastPage.nextCursor : undefined;
    },
    initialPageParam: 1,
  });

  const items = data?.pages.flatMap(page => page.data) ?? [];
  const isLoadingMore = isFetchingNextPage || (isFetching && !isLoading);

  // Infinite scroll with Intersection Observer
  const handleObserver = useCallback((entries: IntersectionObserverEntry[]) => {
    const target = entries[0];
    if (target.isIntersecting && hasNextPage && !isLoadingMore && autoLoad) {
      fetchNextPage();
    }
  }, [hasNextPage, isLoadingMore, fetchNextPage, autoLoad]);

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: "100px",
      threshold: 0,
    });

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => {
      if (loaderRef.current) {
        observer.unobserve(loaderRef.current);
      }
    };
  }, [handleObserver]);

  // Handle filter changes
  const handleFilterChange = (key: keyof FilterFormData, value: string | boolean) => {
    setValue(key, value as any);
    // Reset to first page when filters change
    // The query will automatically refetch due to queryKey change
  };

  // Reset all filters
  const resetFilters = () => {
    methods.reset({
      page: "",
      office: "",
      type: "",
      "start-date": "",
      "end-date": "",
      "open-only": false,
      q: "",
    });
    setShowFilters(false);
  };

  // Handle search input with debounce
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue("q", e.target.value);
  };

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (filters.q !== undefined) {
        refetch();
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [filters.q, refetch]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const favOffices = [
      { label: 'ދިވެހިރާއްޖޭގެ އިސްލާމީ ޔުނިވަރސިޓީ', value: 'ދިވެހިރާއްޖޭގެ އިސްލާމީ ޔުނިވަރސިޓީ', icon: '', shortLabel: 'IUM' },
      { label: 'ދިވެހިރާއްޖޭގެ ޤައުމީ ޔުނިވަރސިޓީ', value: 'ދިވެހިރާއްޖޭގެ ޤައުމީ ޔުނިވަރސިޓީ', icon: '', shortLabel: 'MNU' },
  ]

  return (
    <div className={`dhivehi container mx-auto px-4 py-6 ${isRTL ? 'rtl' : 'ltr'}`} dir={direction}>
      {/* Header */}
      <div className="flex flex-col justify-between items-center mb-6 flex-wrap gap-2">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          {t('gazette')}
        </h1>
        
        <div className="w-full flex gap-2">
          {/* Search Input */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search announcements..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <svg
              className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>

          <button
            onClick={() => handleFilterChange('q', searchTerm)}
            className="px-2 py-2 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          
          </button>

          {/* Filter Toggle Button */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="px-2 py-2 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
          
            {/* {(filters.office || filters.type || filters["start-date"] || filters["end-date"]) && (
              <span className="bg-blue-500 text-white text-xs rounded-full px-2 py-0.5">
                Active
              </span>
            )} */}
          </button>

          {/* Auto-load Toggle */}
          {/* <button
            onClick={() => setAutoLoad(!autoLoad)}
            className="text-sm px-2 py-2 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition"
          >
            {autoLoad ? "🔁 Auto" : "⏸️ Manual"}
          </button> */}
        </div>
      </div>

      <div className=" rounded-lg p-0 mb-6 ">
          <FormProvider {...methods}>
            <div className=" grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              
              <div className="flex flex-wrap w-full justify-between items-center gap-2">
                {/* Office Filter with SearchableSelect */}
              <div className="w-full">
                <label className="block text-sm font-medium mb-2 dark:text-gray-300">
                  Office / Organization
                </label>
                <SearchableSelectField
                  field={{
                    name: "office",
                    label: "Office",
                    type: "searchableselect",
                    options: mvOffices.map(office => ({ 
                      label: office.shortLabel || office.label, 
                      value: office.value
                    })),
                    placeholder: "Search and select office...",
                    clearable: true,
                    showBox: false,
                  }}
                />
              </div>

             





             
              {/* <FilterButtons options={[
                ...mvIulaanTypes.map(type => ({ 
                  value: type.value, 
                  label: type.label 
                })),
              ]} selectedType={filters.type} setSelectedType={(value) => handleFilterChange('type', value)} /> */}

              {/* Iulaan Type Filter with SearchableSelect */}
              {/* <div className="w-1/2">
                <label className="block text-sm font-medium mb-2 dark:text-gray-300">
                  Announcement Type
                </label>
                <SearchableSelectField
                  field={{
                    name: "type",
                    label: "Iulaan Type",
                    type: "searchableselect",
                    options: mvIulaanTypes.map(type => ({ 
                      label: type.label, 
                      value: type.value 
                    })),
                    placeholder: "Search and select iulaan type...",
                    clearable: true,
                    showBox: false,
                  }}
                />
              </div> */}

              


              </div>

          

        

              {/* Open Only Toggle */}
              
            </div>

             <MyHorizontalScrollable>
                 {mvOffices.map((office) => (
                   <IconButton key={office.value}
                      iconPosition="top"
                      active={filters.office === office.value}
                      onClick={() => handleFilterChange('office', office.value)}
                     label={office.shortLabel || office.label}
                     icon={office.icon || "🏢"} />
                  ))}
              </MyHorizontalScrollable>

<MyDivider />

            <MyLabel label="Announcement Type" />

              <MyHorizontalScrollable >
                 {mvIulaanTypes.map((type) => (
                   <IconButton key={type.value}
                      iconPosition="top"
                      active={filters.type === type.value}
                      onClick={() => handleFilterChange('type', type.value)}
                     label={type.label}
                     icon={type?.icon || "🏢"} />
                  ))}
              </MyHorizontalScrollable>

            {/* Filter Actions */}
            <div className="flex justify-between gap-2 mt-4">
              <div className="flex flex-col justify-center items-center">
                <MySwitch 
                  label="Non-Expired" 
                  checked={filters["open-only"]} 
                  onChange={() => handleFilterChange('open-only', !filters["open-only"])}
                />
              </div>
              <button
                onClick={resetFilters}
                type="button"
                className="px-4 py-2 text-xs text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition"
              >
                Reset All
              </button>
             
            </div>
          </FormProvider>
        </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 mb-6 border dark:border-gray-700">
          <FormProvider {...methods}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Office Filter with SearchableSelect */}
      


              {/* Start Date */}
              <div>
                <label className="block text-sm font-medium mb-2 dark:text-gray-300">
                  Start Date From
                </label>
                <input
                  type="date"
                  value={filters["start-date"]}
                  onChange={(e) => handleFilterChange('start-date', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* End Date */}
              <div>
                <label className="block text-sm font-medium mb-2 dark:text-gray-300">
                  End Date To
                </label>
                <input
                  type="date"
                  value={filters["end-date"]}
                  onChange={(e) => handleFilterChange('end-date', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Open Only Toggle */}
              <div className="flex items-center">
                <MySwitch 
                  label="Show only open applications" 
                  checked={filters["open-only"]} 
                  onChange={() => handleFilterChange('open-only', !filters["open-only"])}
                />
              </div>
            </div>

            {/* Filter Actions */}
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={resetFilters}
                type="button"
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition"
              >
                Reset All
              </button>
              <button
                onClick={() => setShowFilters(false)}
                type="button"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                Apply Filters
              </button>
            </div>
          </FormProvider>
        </div>
      )}
      
      {/* Stats */}
      {items.length > 0 && (
        <div className="mb-4 text-sm text-gray-500 dark:text-gray-400">
          Showing {items.length} announcement{items.length !== 1 ? 's' : ''}
          {(filters.q || filters.office || filters.type) && (
            <span className="ml-2">
              (filtered)
              <button
                onClick={resetFilters}
                className="ml-2 text-blue-600 hover:underline"
              >
                Clear filters
              </button>
            </span>
          )}
        </div>
      )}
      
      {/* Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((item, idx) => (
          <AnnouncementCard key={`${item.page}-${idx}-${item.link}`} item={item} />
        ))}
      </div>
      
      {/* Load more section */}
      <div ref={loaderRef} className="py-8">
        {hasNextPage ? (
          <>
            {isLoadingMore ? (
              <div className="flex flex-col items-center gap-2">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="text-sm text-gray-500">Loading more...</span>
              </div>
            ) : (
              <div className="flex justify-center">
                <button
                  onClick={() => fetchNextPage()}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-md"
                >
                  Load More
                </button>
              </div>
            )}
          </>
        ) : items.length > 0 ? (
          <div className="text-center py-4">
            <p className="text-gray-500">✨ You've seen all announcements ✨</p>
          </div>
        ) : null}
      </div>
      
      {/* Empty state */}
      {items.length === 0 && !isLoading && (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">📢</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No announcements found</h3>
          <p className="text-gray-500">
            {filters.q || filters.office || filters.type 
              ? "Try adjusting your filters or search term"
              : "Check back later for new announcements"}
          </p>
          {(filters.q || filters.office || filters.type) && (
            <button
              onClick={resetFilters}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
            >
              Clear all filters
            </button>
          )}
        </div>
      )}
    </div>
  );
}